import { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Save, X, Loader2, Calendar, Repeat, FileText, Search, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { authHeaders } from "@/lib/firebase";
import TopNav from "@/components/TopNav";
import HostLayout from "@/components/host/HostLayout";
import CoverImagePicker from "@/components/CoverImagePicker";
import SportPicker from "@/components/SportPicker";
import { useDraft } from "@/hooks/useDraft";

const UK_CITIES = ["London", "Manchester", "Birmingham", "Liverpool", "Edinburgh", "Bristol", "Leeds", "Glasgow"];
const EU_CITIES = ["Berlin", "Amsterdam", "Paris"];
const currencyForCity = (city) => UK_CITIES.includes(city) ? "GBP" : EU_CITIES.includes(city) ? "EUR" : "USD";
const currencySymbol = (city) => UK_CITIES.includes(city) ? "£" : EU_CITIES.includes(city) ? "€" : "$";

const sportImages = {
  basketball: "https://images.unsplash.com/photo-1546519638-68e109498ffc?w=800",
  football: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800",
  padel: "https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=800",
  volleyball: "https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?w=800",
};

const WEEKDAYS = [
  { code: "MON", short: "Mon" },
  { code: "TUE", short: "Tue" },
  { code: "WED", short: "Wed" },
  { code: "THU", short: "Thu" },
  { code: "FRI", short: "Fri" },
  { code: "SAT", short: "Sat" },
  { code: "SUN", short: "Sun" },
];
const WEEKDAY_INDEX = { MON: 0, TUE: 1, WED: 2, THU: 3, FRI: 4, SAT: 5, SUN: 6 };

// Nominatim geocoder — mirrors the helper used on CreateGamePage so hosts get the
// same address-lookup + map-preview experience for series.
const geocodeAddress = async (address, city) => {
  try {
    const query = encodeURIComponent(`${address}, ${city}`);
    const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1`, {
      headers: { "Accept-Language": "en" },
    });
    const data = await res.json();
    if (data && data.length > 0) {
      return { latitude: parseFloat(data[0].lat), longitude: parseFloat(data[0].lon), displayName: data[0].display_name };
    }
    return null;
  } catch (_) {
    return null;
  }
};

function previewSessionDates(startStr, endStr, freq, days, interval) {
  if (!startStr || !endStr) return [];
  const start = new Date(startStr);
  const end = new Date(endStr);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
  if (end < start) return [];
  const out = [];
  const cap = 200;
  if (freq === "weekly") {
    const targetDays = (days && days.length > 0)
      ? new Set(days.map(d => WEEKDAY_INDEX[d]))
      : new Set([(start.getDay() + 6) % 7]);
    const cur = new Date(start);
    while (cur <= end && out.length < cap) {
      const jsDay = (cur.getDay() + 6) % 7;
      if (targetDays.has(jsDay)) out.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
  } else if (freq === "monthly") {
    const cur = new Date(start);
    while (cur <= end && out.length < cap) {
      out.push(new Date(cur));
      cur.setMonth(cur.getMonth() + 1);
    }
  } else {
    const step = Math.max(1, parseInt(interval || 1, 10));
    const cur = new Date(start);
    while (cur <= end && out.length < cap) {
      out.push(new Date(cur));
      cur.setDate(cur.getDate() + step);
    }
  }
  return out;
}

const emptySeries = {
  sport: "basketball",
  title: "",
  description: "",
  city: "London",
  address: "",
  location_name: "",
  latitude: 0,
  longitude: 0,
  time_start: "",
  time_end: "",
  max_players: 10,
  price_per_session: 0,
  image_url: sportImages.basketball,
  skill_level: "all",
  host_contact: "",
  meeting_info: "",
  freq: "weekly",
  days: ["TUE"],
  interval: 7,
  start_date: "",
  end_date: "",
  season_pass_enabled: true,
  season_pass_price: 0,
};

const SINGLE_SESSION_MARKUP = 1.35;
const computeSinglePrice = (passPrice, totalSessions) => {
  const p = parseFloat(passPrice) || 0;
  const n = parseInt(totalSessions, 10) || 0;
  if (p <= 0 || n <= 0) return 0;
  return Math.ceil((p / n) * SINGLE_SESSION_MARKUP);
};

const CreateSeriesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const isEditMode = !!editId;
  const [form, setForm] = useState(emptySeries);
  const [creating, setCreating] = useState(false);
  const [perSessionTouched, setPerSessionTouched] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(isEditMode);
  const { saveDraft, discardDraft, saving: savingDraft, autoSaveStatus } = useDraft("series", {
    onLoad: (payload) => setForm({ ...emptySeries, ...payload }),
    autoSaveData: form,
    autoSaveEnabled: !isEditMode,
  });

  // Prefill when editing an existing series
  useEffect(() => {
    if (!isEditMode) return;
    let cancelled = false;
    (async () => {
      try {
        const headers = await authHeaders();
        const res = await fetch(`${API}/series/${editId}`, { headers });
        if (!res.ok) {
          toast.error("Couldn't load series to edit");
          navigate("/host");
          return;
        }
        const s = await res.json();
        if (cancelled) return;
        setForm({
          ...emptySeries,
          sport: s.sport || emptySeries.sport,
          title: s.title || "",
          description: s.description || "",
          city: s.city || emptySeries.city,
          location_name: s.location_name || "",
          address: s.address || "",
          latitude: s.latitude || 0,
          longitude: s.longitude || 0,
          image_url: s.image_url || "",
          start_date: s.start_date ? new Date(s.start_date).toISOString().slice(0, 10) : "",
          end_date: s.end_date ? new Date(s.end_date).toISOString().slice(0, 10) : "",
          time_start: s.time_start || "",
          time_end: s.time_end || "",
          freq: s.recurrence?.freq || "WEEKLY",
          interval: s.recurrence?.interval || 1,
          days: s.recurrence?.days || [],
          skill_level: s.skill_level || "all",
          max_players: s.max_players || 8,
          price_per_session: s.price_per_session || 0,
          season_pass_enabled: !!s.season_pass_enabled,
          season_pass_price: s.season_pass_price || 0,
        });
        // Once prefilled, treat any subsequent user changes as manual overrides
        setPerSessionTouched(true);
      } catch (_) {
        toast.error("Network error");
      } finally {
        if (!cancelled) setLoadingEdit(false);
      }
    })();
    return () => { cancelled = true; };
  }, [editId, isEditMode, navigate]);

  // Prefill start date/time from the Schedule drag-to-create flow
  useEffect(() => {
    if (isEditMode) return;
    const d = searchParams.get("date"), s = searchParams.get("start"), e = searchParams.get("end");
    if (!d && !s && !e) return;
    setForm((prev) => ({ ...prev, ...(d ? { start_date: d } : {}), ...(s ? { time_start: s } : {}), ...(e ? { time_end: e } : {}) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sessionDates = useMemo(
    () => previewSessionDates(form.start_date, form.end_date, form.freq, form.days, form.interval),
    [form.start_date, form.end_date, form.freq, form.days, form.interval]
  );

  useEffect(() => {
    if (!form.season_pass_enabled) return;
    if (perSessionTouched) return;
    const calc = computeSinglePrice(form.season_pass_price, sessionDates.length);
    setForm(prev => ({ ...prev, price_per_session: calc }));
  }, [form.season_pass_price, form.season_pass_enabled, sessionDates.length, perSessionTouched]);

  const toggleDay = (code) => {
    setForm(prev => {
      const has = prev.days.includes(code);
      return { ...prev, days: has ? prev.days.filter(d => d !== code) : [...prev.days, code] };
    });
  };

  const setSport = (sport) => setForm(prev => ({ ...prev, sport, image_url: sportImages[sport] || prev.image_url }));

  const handleSubmit = async () => {
    if (creating) return;
    if (!form.title || !form.start_date || !form.end_date || !form.time_start || !form.time_end || !form.city || !form.location_name) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (sessionDates.length === 0) {
      toast.error("Your recurrence rule produces 0 sessions. Check the dates and frequency.");
      return;
    }
    setCreating(true);
    try {
      const start = new Date(form.start_date);
      const end = new Date(form.end_date);
      const payload = {
        sport: form.sport,
        title: form.title,
        description: form.description,
        location_name: form.location_name,
        city: form.city,
        address: form.address,
        latitude: form.latitude || 0,
        longitude: form.longitude || 0,
        time_start: form.time_start,
        time_end: form.time_end,
        max_players: parseInt(form.max_players, 10) || 10,
        price_per_session: parseFloat(form.price_per_session) || 0,
        currency: currencyForCity(form.city),
        image_url: form.image_url,
        host_name: user?.name || "Host",
        host_image_url: user?.picture || "",
        skill_level: form.skill_level,
        host_contact: form.host_contact || null,
        meeting_info: form.meeting_info || null,
        recurrence: {
          freq: form.freq,
          days: form.freq === "weekly" ? form.days : [],
          interval: form.freq === "custom" ? (parseInt(form.interval, 10) || 7) : 1,
        },
        start_date: start.toISOString(),
        end_date: end.toISOString(),
        season_pass_enabled: !!form.season_pass_enabled,
        season_pass_price: form.season_pass_enabled ? (parseFloat(form.season_pass_price) || 0) : 0,
      };
      const headers = await authHeaders({ "Content-Type": "application/json" });
      const url = isEditMode ? `${API}/series/${editId}` : `${API}/series`;
      const method = isEditMode ? "PUT" : "POST";
      const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      if (res.ok) {
        const data = await res.json();
        if (isEditMode) {
          toast.success("Series updated");
          navigate(`/host/manage/series/${data.series_id || editId}`);
        } else {
          toast.success(`Series created with ${sessionDates.length} sessions!`);
          await discardDraft();
          navigate(`/series/${data.series_id}`);
        }
      } else {
        const detail = await res.json().then(d => d.detail).catch(() => null);
        toast.error(detail || `Failed to ${isEditMode ? "update" : "create"} series`);
      }
    } catch (e) {
      toast.error(`Failed to ${isEditMode ? "update" : "create"} series`);
    } finally {
      setCreating(false);
    }
  };

  const fromHost = searchParams.get("from") === "host";
  const backTo = isEditMode ? `/host/manage/series/${editId}` : (fromHost ? "/host/events" : "/profile");
  const content = (
    <>
      <div data-testid="create-series-page">
        <button
          onClick={() => navigate(backTo)}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"
          data-testid="create-series-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {loadingEdit && (
          <div className="py-8 flex items-center justify-center gap-2 text-muted-foreground" data-testid="edit-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading series…
          </div>
        )}

        <div className="mb-6">
          <h1 className="font-display text-3xl sm:text-4xl flex items-center gap-2">
            <Repeat className="w-7 h-7 text-primary" /> {isEditMode ? "Edit series" : "Host a recurring series"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isEditMode
              ? "Update the details below. Existing sessions and season-pass holders keep their spots."
              : "Build a recurring class or club. Individual sessions stay bookable; guests can also buy a pass."}
          </p>
        </div>

        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="series-sport">Activity type</Label>
              <SportPicker value={form.sport} onChange={setSport} testid="series-sport" />
            </div>
            <div>
              <Label htmlFor="series-skill">Level</Label>
              <Select value={form.skill_level} onValueChange={(v) => setForm({ ...form, skill_level: v })}>
                <SelectTrigger id="series-skill"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All levels</SelectItem>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="series-title">Title *</Label>
            <Input id="series-title" data-testid="series-title" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Tuesday Football League" />
          </div>

          <CoverImagePicker
            value={form.image_url}
            onChange={(url) => setForm({ ...form, image_url: url })}
            label="Banner image"
            helper="Shown on the homepage and at the top of your page. Or pick a type above to use the default."
            testidPrefix="series-cover"
          />

          <div>
            <Label htmlFor="series-desc">Description</Label>
            <Textarea id="series-desc" rows={3} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What's the vibe? Format, rules, who it's for…" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="series-city">City *</Label>
              <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v })}>
                <SelectTrigger id="series-city"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[...UK_CITIES, ...EU_CITIES, "New York", "Los Angeles", "Miami"].map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="series-location">Venue name *</Label>
              <Input id="series-location" value={form.location_name}
                onChange={(e) => setForm({ ...form, location_name: e.target.value })}
                placeholder="Hyde Park Courts" />
            </div>
          </div>

          <div>
            <Label htmlFor="series-address">Address</Label>
            <div className="flex gap-2">
              <Input
                id="series-address"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value, latitude: 0, longitude: 0 })}
                placeholder="Street, postcode"
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={async () => {
                  if (!form.address) {
                    toast.error("Enter an address first");
                    return;
                  }
                  toast.loading("Looking up address...", { id: "series-geocode" });
                  const result = await geocodeAddress(form.address, form.city);
                  if (result) {
                    setForm({ ...form, latitude: result.latitude, longitude: result.longitude });
                    toast.success("Location found!", { id: "series-geocode" });
                  } else {
                    toast.error("Couldn't find that address. Try being more specific.", { id: "series-geocode" });
                  }
                }}
                className="shrink-0"
                data-testid="series-address-find"
              >
                <Search className="w-4 h-4 mr-1" /> Find
              </Button>
            </div>
            {form.latitude !== 0 && form.longitude !== 0 && (
              <div className="mt-2 rounded-lg overflow-hidden border border-border" data-testid="series-map-preview">
                <iframe
                  title="Location Preview"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${form.longitude - 0.003},${form.latitude - 0.002},${form.longitude + 0.003},${form.latitude + 0.002}&layer=mapnik&marker=${form.latitude},${form.longitude}`}
                  className="w-full h-28"
                  style={{ border: 0 }}
                />
                <p className="text-xs text-green-600 px-2 py-1.5 bg-green-50 dark:bg-green-950/30 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Location verified - this is where the pin will show
                </p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="series-tstart">Start time *</Label>
              <Input id="series-tstart" type="time" value={form.time_start}
                onChange={(e) => setForm({ ...form, time_start: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="series-tend">End time *</Label>
              <Input id="series-tend" type="time" value={form.time_end}
                onChange={(e) => setForm({ ...form, time_end: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="series-max">Capacity</Label>
              <Input id="series-max" type="number" min={1} value={form.max_players}
                onChange={(e) => setForm({ ...form, max_players: e.target.value })} />
            </div>
          </div>

          <div className="rounded-xl border border-border p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-primary" />
              <h4 className="font-display text-base">Schedule</h4>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="series-freq">Frequency</Label>
                <Select value={form.freq} onValueChange={(v) => setForm({ ...form, freq: v })}>
                  <SelectTrigger id="series-freq" data-testid="series-freq"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="custom">Custom (every N days)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.freq === "custom" && (
                <div>
                  <Label htmlFor="series-interval">Every N days</Label>
                  <Input id="series-interval" type="number" min={1} value={form.interval}
                    onChange={(e) => setForm({ ...form, interval: e.target.value })} />
                </div>
              )}
            </div>
            {form.freq === "weekly" && (
              <div>
                <Label>Days of the week</Label>
                <div className="flex flex-wrap gap-2 mt-1.5" data-testid="series-days">
                  {WEEKDAYS.map(d => {
                    const active = form.days.includes(d.code);
                    return (
                      <button
                        type="button"
                        key={d.code}
                        onClick={() => toggleDay(d.code)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          active ? "bg-primary text-primary-foreground border-primary" : "bg-muted/40 border-border hover:border-primary/50"
                        }`}
                      >
                        {d.short}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="series-start">Start date *</Label>
                <Input id="series-start" type="date" value={form.start_date}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="series-end">End date *</Label>
                <Input id="series-end" type="date" value={form.end_date}
                  min={form.start_date || new Date().toISOString().split("T")[0]}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </div>
            </div>
            <div className="rounded-lg bg-muted/40 p-3 text-sm" data-testid="series-preview">
              {sessionDates.length === 0 ? (
                <p className="text-muted-foreground">Fill in dates above to preview your sessions.</p>
              ) : (
                <>
                  <p className="font-medium mb-1.5">
                    This will create {sessionDates.length} session{sessionDates.length !== 1 ? "s" : ""}:
                  </p>
                  <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                    {sessionDates.slice(0, 30).map((d, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-md bg-card border border-border text-xs">
                        {d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
                      </span>
                    ))}
                    {sessionDates.length > 30 && (
                      <span className="px-2 py-0.5 text-xs text-muted-foreground">+{sessionDates.length - 30} more</span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Pricing */}
          <div className="rounded-xl border border-border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-display text-base">Pricing</h4>
              <span className="text-xs text-muted-foreground">{currencySymbol(form.city)} {form.city}</span>
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox
                checked={form.season_pass_enabled}
                onCheckedChange={(v) => {
                  setForm(prev => ({ ...prev, season_pass_enabled: !!v }));
                  setPerSessionTouched(false);
                }}
                data-testid="series-pass-toggle"
              />
              <div className="flex-1">
                <p className="font-medium leading-tight">Offer a season pass</p>
                <p className="text-xs text-muted-foreground">Guests pay once and are auto-confirmed for every session. Pro-rated for late joiners.</p>
              </div>
            </label>
            {form.season_pass_enabled ? (
              <div className="space-y-3 pt-1">
                <div>
                  <Label htmlFor="series-passprice">Season pass price ({currencySymbol(form.city)}) *</Label>
                  <Input
                    id="series-passprice"
                    type="number"
                    step="0.01"
                    min={0}
                    value={form.season_pass_price}
                    onChange={(e) => setForm({ ...form, season_pass_price: e.target.value })}
                    placeholder="e.g. 60"
                    data-testid="series-passprice"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Total a player pays once to attend every session.</p>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label htmlFor="series-price">Price per single session ({currencySymbol(form.city)})</Label>
                    {perSessionTouched && (
                      <button
                        type="button"
                        onClick={() => setPerSessionTouched(false)}
                        className="text-xs text-foreground/70 hover:text-foreground underline"
                      >
                        Reset to auto
                      </button>
                    )}
                  </div>
                  {(() => {
                    const pass = parseFloat(form.season_pass_price) || 0;
                    const n = sessionDates.length;
                    const per = parseFloat(form.price_per_session) || 0;
                    const ready = pass > 0 && n > 0;
                    return (
                      <>
                        <Input
                          id="series-price"
                          type="number"
                          step="0.01"
                          min={0}
                          value={ready ? form.price_per_session : ""}
                          placeholder={ready ? "" : "—"}
                          disabled={!ready}
                          onChange={(e) => {
                            setPerSessionTouched(true);
                            setForm({ ...form, price_per_session: e.target.value });
                          }}
                          data-testid="series-price"
                          className={!ready ? "bg-muted/50 cursor-not-allowed" : ""}
                        />
                        {!ready ? (
                          <p className="text-xs text-muted-foreground mt-1">
                            {pass <= 0 ? "Enter a season pass price above…" : "Pick a start and end date in the Schedule above to auto-calculate this."}
                          </p>
                        ) : (() => {
                          const totalIfSingles = per * n;
                          const savings = Math.max(0, totalIfSingles - pass);
                          return (
                            <p className="text-xs text-muted-foreground mt-1" data-testid="series-savings-caption">
                              {currencySymbol(form.city)}{pass.toFixed(0)} pass for {n} session{n !== 1 ? "s" : ""} ·
                              single sessions {currencySymbol(form.city)}{per.toFixed(2)}
                              {savings > 0 && <> (pass holders save {currencySymbol(form.city)}{savings.toFixed(0)} over the {n === 1 ? "session" : "season"})</>}
                              {!perSessionTouched && <span className="italic"> · auto-calculated, you can edit.</span>}
                            </p>
                          );
                        })()}
                      </>
                    );
                  })()}
                </div>
              </div>
            ) : (
              <div>
                <Label htmlFor="series-price">Price per session ({currencySymbol(form.city)})</Label>
                <Input
                  id="series-price"
                  type="number"
                  step="0.01"
                  min={0}
                  value={form.price_per_session}
                  onChange={(e) => {
                    setPerSessionTouched(true);
                    setForm({ ...form, price_per_session: e.target.value });
                  }}
                  data-testid="series-price"
                />
                <p className="text-xs text-muted-foreground mt-1">Set 0 for free sessions.</p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3">
            <div>
              <Label htmlFor="series-contact">Public host contact (optional)</Label>
              <Input id="series-contact" value={form.host_contact}
                onChange={(e) => setForm({ ...form, host_contact: e.target.value })}
                placeholder="@yourhandle, phone, email…" />
            </div>
            <div>
              <Label htmlFor="series-meeting">Meeting info (only attendees see this)</Label>
              <Textarea id="series-meeting" rows={2} value={form.meeting_info}
                onChange={(e) => setForm({ ...form, meeting_info: e.target.value })}
                placeholder="Court 3, look for the orange flag, bring £5 cash for balls…" />
            </div>
          </div>
        </div>
      </div>

      <div className={`fixed bottom-0 inset-x-0 border-t border-border bg-background/95 backdrop-blur z-40 ${fromHost ? "lg:left-60" : ""}`}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <Button variant="ghost" onClick={() => navigate(backTo)} className="gap-2">
            <X className="w-4 h-4" /> Cancel
          </Button>
          <div className="flex items-center gap-2">
            {!isEditMode && autoSaveStatus !== "idle" && (
              <span className="text-xs text-muted-foreground inline-flex items-center gap-1" data-testid="autosave-indicator">
                {autoSaveStatus === "saving" ? (
                  <><Loader2 className="w-3 h-3 animate-spin" /> Auto-saving…</>
                ) : (
                  <>Draft saved</>
                )}
              </span>
            )}
            {!isEditMode && (
              <Button
                variant="outline"
                onClick={() => saveDraft(form)}
                disabled={savingDraft || creating}
                className="gap-2"
                data-testid="save-series-draft-btn"
              >
                {savingDraft ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><FileText className="w-4 h-4" /> Save draft</>}
              </Button>
            )}
            <Button onClick={handleSubmit} disabled={creating || loadingEdit} className="bg-primary text-primary-foreground hover:bg-primary/90 gap-2" data-testid="submit-series-btn">
              {creating ? <><Loader2 className="w-4 h-4 animate-spin" /> {isEditMode ? "Saving…" : "Creating…"}</> : <><Save className="w-4 h-4" /> {isEditMode ? "Save changes" : "Create series"}</>}
            </Button>
          </div>
        </div>
      </div>
    </>
  );

  return fromHost
    ? <HostLayout><div className="max-w-3xl mx-auto pb-28">{content}</div></HostLayout>
    : <div className="min-h-screen bg-background"><TopNav /><main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 pb-32">{content}</main></div>;
};

export default CreateSeriesPage;
